import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  MapPin,
  CheckCircle2,
  Users,
  Calendar,
  AlertCircle,
  UserPlus,
  Download,
  TrendingUp,
  Clock,
  Target,
  XCircle,
  BarChart3
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin) || '';

// Church coordinates - C6V7+XF Dartford
const CHURCH_COORDS = { lat: 51.444628, lng: 0.213729 };
const CHURCH_RADIUS = 100; // meters

// Utility: Calculate distance using Haversine formula
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

// Check if it's Sunday
function isSunday() {
  return new Date().getDay() === 0;
}

// Check if time is between 10 AM and 6 PM
function isWithinServiceHours() {
  const now = new Date();
  const hour = now.getHours();
  return hour >= 10 && hour < 18;
}

function GPSAttendancePage({ isAuthenticated, memberId }) {
  // Silent GPS tracking state (invisible to members)
  const [watchId, setWatchId] = useState(null);
  const [attendanceRecorded, setAttendanceRecorded] = useState(false);
  
  // Admin dashboard state
  const [activeTab, setActiveTab] = useState('today');
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [flaggedMembers, setFlaggedMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  
  // Manual Entry (Extra Head Count) state
  const [manualEntries, setManualEntries] = useState([]);
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [manualEntryData, setManualEntryData] = useState({
    full_name: '',
    phone: '',
    category: 'visitor',
    notes: ''
  });
  
  // Silent GPS tracking - Only runs on Sunday 10 AM - 6 PM
  useEffect(() => {
    if (!memberId || attendanceRecorded) return;
    
    // Only start tracking on Sunday between 10 AM - 6 PM
    if (!isSunday() || !isWithinServiceHours()) {
      return;
    }
    
    // Check if already checked in today
    const checkedInToday = localStorage.getItem(`checked_in_${new Date().toISOString().slice(0, 10)}`);
    if (checkedInToday === 'true') {
      setAttendanceRecorded(true);
      return;
    }
    
    console.log('🎯 Silent GPS tracking started (Sunday 10 AM - 6 PM)');
    
    if ('geolocation' in navigator) {
      const watchIdValue = navigator.geolocation.watchPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const distance = calculateDistance(latitude, longitude, CHURCH_COORDS.lat, CHURCH_COORDS.lng);
          
          console.log(`📍 Current distance from church: ${Math.round(distance)}m`);
          
          // Automatically check in if within church radius
          if (distance <= CHURCH_RADIUS && !attendanceRecorded) {
            console.log('✅ Within church radius! Auto-checking in...');
            await silentCheckIn(latitude, longitude);
          }
        },
        (error) => {
          console.error('GPS tracking error:', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          timeout: 27000
        }
      );
      
      setWatchId(watchIdValue);
    }
    
    // Cleanup on unmount
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        console.log('🛑 GPS tracking stopped');
      }
    };
  }, [memberId, attendanceRecorded, watchId]);
  
  const silentCheckIn = async (lat, lng) => {
    try {
      const response = await axios.post(`${API}/api/attendance/check-in`, {
        member_id: memberId,
        lat,
        lng,
        check_in_method: 'auto'
      });
      
      console.log('✅ Attendance recorded successfully (silent)');
      setAttendanceRecorded(true);
      localStorage.setItem(`checked_in_${new Date().toISOString().slice(0, 10)}`, 'true');
      
      // Stop GPS tracking after successful check-in
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        setWatchId(null);
      }
    } catch (error) {
      console.error('Silent check-in failed:', error);
      // Don't show errors to user - this is silent
    }
  };
  
  // Admin: Load dashboard data
  useEffect(() => {
    if (isAuthenticated) {
      loadAdminDashboard();
    }
  }, [isAuthenticated]);
  
  const loadAdminDashboard = async () => {
    try {
      // Load today's attendance
      const todayRes = await axios.get(`${API}/api/attendance/today`);
      setTodayAttendance(todayRes.data.attendance || []);
      
      // Load attendance statistics
      const statsRes = await axios.get(`${API}/api/attendance/stats`);
      setStats(statsRes.data);
      
      // Load flagged members
      const flaggedRes = await axios.get(`${API}/api/attendance/flagged-members`);
      setFlaggedMembers(flaggedRes.data || []);
      
      // Load all members
      const membersRes = await axios.get(`${API}/api/members`);
      setAllMembers(membersRes.data || []);
      
      // Load manual entries
      const manualRes = await axios.get(`${API}/api/attendance/manual-entries/today`);
      setManualEntries(manualRes.data.entries || []);
    } catch (error) {
      console.error('Error loading admin dashboard:', error);
    }
  };
  
  const addManualEntry = async () => {
    if (!manualEntryData.full_name || !manualEntryData.category) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/api/attendance/manual-entry`, {
        full_name: manualEntryData.full_name,
        phone: manualEntryData.phone || '',
        category: manualEntryData.category,
        notes: manualEntryData.notes || ''
      });
      
      alert('Manual entry added successfully!');
      setManualEntryData({ full_name: '', phone: '', category: 'visitor', notes: '' });
      setShowManualEntryForm(false);
      loadAdminDashboard();
    } catch (error) {
      console.error('Error adding manual entry:', error);
      alert('Failed to add manual entry');
    }
  };
  
  const loadAttendanceHistory = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${API}/api/attendance/history?${params}`);
      setAttendanceHistory(response.data || []);
    } catch (error) {
      console.error('Error loading attendance history:', error);
    }
  };
  
  // FOR MEMBERS: Show nothing (completely invisible)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Attendance Tracking Active</h2>
          <p className="text-slate-600 max-w-md mx-auto">
            Your attendance is automatically tracked when you're at church during service hours (Sundays, 10 AM - 6 PM).
          </p>
          <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-200 max-w-md mx-auto">
            <p className="text-sm text-blue-800">
              <strong>GPS tracking is active.</strong> No action needed from you - just attend service and your presence will be recorded automatically! 🙏
            </p>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // FOR ADMINS: Show full dashboard
  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 p-6 md:p-8 text-white shadow-2xl"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl translate-y-24 -translate-x-24"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <motion.div 
              className="p-3 md:p-4 rounded-2xl bg-white/20 backdrop-blur-md"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
            >
              <BarChart3 className="w-6 h-6 md:w-8 md:h-8" />
            </motion.div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold mb-1">Attendance Dashboard</h1>
              <p className="text-blue-100 text-sm md:text-base">GPS tracking & member attendance analytics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.div 
              className="flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-2xl px-4 py-3"
              whileHover={{ scale: 1.02 }}
            >
              <Users className="w-5 h-5 md:w-6 md:h-6" />
              <div className="text-left">
                <p className="text-xs text-blue-100">Today's Count</p>
                <p className="text-xl md:text-2xl font-bold">{todayAttendance.length + manualEntries.length}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
      
      {/* Stats Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { label: "This Week", value: stats.this_week || 0, icon: Calendar, color: "from-blue-500 to-cyan-500", bg: "from-blue-50 to-cyan-50" },
            { label: "This Month", value: stats.this_month || 0, icon: TrendingUp, color: "from-emerald-500 to-teal-500", bg: "from-emerald-50 to-teal-50" },
            { label: "Total Members", value: allMembers.length, icon: Users, color: "from-violet-500 to-purple-500", bg: "from-violet-50 to-purple-50" },
            { label: "Flagged", value: flaggedMembers.length, icon: AlertCircle, color: "from-amber-500 to-orange-500", bg: "from-amber-50 to-orange-50" }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className={`p-4 rounded-2xl bg-gradient-to-br ${stat.bg} border border-slate-200 hover:shadow-lg transition-all`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl bg-gradient-to-br ${stat.color}`}>
                  <stat.icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-slate-800">{stat.value}</p>
                  <p className="text-xs md:text-sm text-slate-600">{stat.label}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
      
      {/* Tab Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-2 md:p-3 shadow-lg border border-slate-100 overflow-x-auto"
      >
        <div className="flex gap-2 min-w-max">
          {[
            { id: 'today', label: "Today", icon: Clock, color: "from-blue-500 to-cyan-500" },
            { id: 'history', label: 'History', icon: Calendar, color: "from-violet-500 to-purple-500" },
            { id: 'flagged', label: 'Flagged', icon: AlertCircle, color: "from-red-500 to-rose-500" },
            { id: 'members', label: 'Members', icon: Users, color: "from-emerald-500 to-teal-500" },
            { id: 'manual', label: 'Manual Entry', icon: UserPlus, color: "from-amber-500 to-orange-500" }
          ].map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm">{tab.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>
      
      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'today' && (
          <motion.div
            key="today"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Today's Attendance</h2>
            </div>
            
            {todayAttendance.length === 0 && manualEntries.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <Calendar className="w-10 h-10 text-slate-300" />
                </div>
                <p className="text-slate-600 font-medium">No attendance records for today</p>
                <p className="text-sm text-slate-500 mt-1">Records will appear here once members check in</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* GPS Check-ins Section */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-semibold text-slate-700">GPS Check-ins ({todayAttendance.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {todayAttendance.map((record, index) => (
                      <motion.div
                        key={record.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-emerald-900">{record.member_name}</h4>
                            <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3" />
                              {new Date(record.check_in_time).toLocaleTimeString()}
                              <span className="px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 text-xs font-medium">
                                {record.check_in_method}
                              </span>
                            </p>
                          </div>
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {/* Manual Entries Section */}
                {manualEntries.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <UserPlus className="w-4 h-4 text-blue-600" />
                      <h3 className="font-semibold text-slate-700">Manual Entries ({manualEntries.length})</h3>
                    </div>
                    <div className="space-y-3">
                      {manualEntries.map((entry, index) => (
                        <motion.div
                          key={entry.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-blue-900">{entry.full_name}</h4>
                              <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                                <span className="px-2 py-0.5 rounded-full bg-blue-200 text-blue-800 text-xs font-medium capitalize">
                                  {entry.category}
                                </span>
                                {entry.phone && <span>{entry.phone}</span>}
                              </p>
                              {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                            </div>
                            <UserPlus className="w-6 h-6 text-blue-600" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
        
        {activeTab === 'flagged' && (
          <motion.div
            key="flagged"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-rose-500">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Flagged Absences</h2>
                <p className="text-sm text-slate-500">Members with 3+ consecutive absences</p>
              </div>
            </div>
            
            {flaggedMembers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <p className="text-slate-600 font-medium">No flagged absences</p>
                <p className="text-sm text-slate-500 mt-1">All members are attending regularly</p>
              </div>
            ) : (
              <div className="space-y-3">
                {flaggedMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-red-900">{member.full_name}</h4>
                        <p className="text-sm text-slate-600">{member.phone} • {member.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-3 py-1 rounded-full bg-red-200 text-red-800 text-xs font-semibold">
                            Missed {member.missed_sundays} Sundays
                          </span>
                          {member.last_attendance && (
                            <span className="text-xs text-slate-500">
                              Last seen: {new Date(member.last_attendance).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
        
        {activeTab === 'members' && (
          <motion.div
            key="members"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">All Members</h2>
                  <p className="text-sm text-slate-500">{allMembers.length} registered members</p>
                </div>
              </div>
              <motion.button
                onClick={loadAdminDashboard}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <TrendingUp className="w-4 h-4" />
                Refresh
              </motion.button>
            </div>
            
            {allMembers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <Users className="w-10 h-10 text-slate-300" />
                </div>
                <p className="text-slate-600 font-medium">No registered members yet</p>
                <p className="text-sm text-slate-500 mt-1">Members will appear here after registration</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {allMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                        {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800 truncate">{member.full_name}</h4>
                        <p className="text-sm text-slate-600 truncate">{member.phone}</p>
                        {member.email && <p className="text-sm text-slate-500 truncate">{member.email}</p>}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                      <span>Joined {new Date(member.registered_at).toLocaleDateString()}</span>
                      <span className={`px-2 py-0.5 rounded-full ${
                        member.missed_sundays > 2 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {member.missed_sundays || 0} missed
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
        
        {activeTab === 'manual' && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Manual Entry</h2>
                  <p className="text-sm text-slate-500">Add visitors and guests manually</p>
                </div>
              </div>
              <motion.button
                onClick={() => setShowManualEntryForm(!showManualEntryForm)}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  showManualEntryForm 
                    ? 'bg-slate-200 text-slate-700' 
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <UserPlus className="w-4 h-4" />
                {showManualEntryForm ? 'Cancel' : 'Add Entry'}
              </motion.button>
            </div>
            
            {showManualEntryForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
              >
                <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  New Manual Entry
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <input
                    className="border-2 border-amber-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all outline-none bg-white"
                    placeholder="Full Name *"
                    value={manualEntryData.full_name}
                    onChange={e => setManualEntryData(prev => ({ ...prev, full_name: e.target.value }))}
                  />
                  <input
                    className="border-2 border-amber-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all outline-none bg-white"
                    placeholder="Phone (optional)"
                    value={manualEntryData.phone}
                    onChange={e => setManualEntryData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                  <select
                    className="border-2 border-amber-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all outline-none bg-white"
                    value={manualEntryData.category}
                    onChange={e => setManualEntryData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="visitor">Visitor</option>
                    <option value="guest">Guest</option>
                    <option value="first_timer">First Timer</option>
                    <option value="child">Child</option>
                  </select>
                  <input
                    className="border-2 border-amber-200 rounded-xl px-4 py-3 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all outline-none bg-white"
                    placeholder="Notes (optional)"
                    value={manualEntryData.notes}
                    onChange={e => setManualEntryData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
                <motion.button
                  onClick={addManualEntry}
                  className="mt-4 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Save Entry
                </motion.button>
              </motion.div>
            )}
            
            <div className="space-y-3">
              {manualEntries.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                    <UserPlus className="w-10 h-10 text-amber-400" />
                  </div>
                  <p className="text-slate-600 font-medium">No manual entries for today</p>
                  <p className="text-sm text-slate-500 mt-1">Click "Add Entry" to record visitors and guests</p>
                </div>
              ) : (
                manualEntries.map((entry, index) => (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-amber-900">{entry.full_name}</h4>
                        <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-xs font-medium capitalize">
                            {entry.category}
                          </span>
                          {entry.phone && <span>{entry.phone}</span>}
                        </p>
                        {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">
                          Added at {new Date(entry.date).toLocaleString()}
                        </p>
                      </div>
                      <UserPlus className="w-6 h-6 text-amber-500" />
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}
        
        {activeTab === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Attendance History</h2>
                  <p className="text-sm text-slate-500">View past attendance records</p>
                </div>
              </div>
              <motion.button
                onClick={() => loadAttendanceHistory()}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Download className="w-4 h-4" />
                Load History
              </motion.button>
            </div>
            
            {attendanceHistory.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Click "Load History" to view past attendance</p>
              </div>
            ) : (
              <div className="space-y-4">
                {attendanceHistory.map((record) => (
                  <div
                    key={record.id}
                    className="p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{record.member_name}</h4>
                        <p className="text-sm text-slate-600">
                          {new Date(record.attendance_date).toLocaleDateString()} at {new Date(record.check_in_time).toLocaleTimeString()}
                        </p>
                        <p className="text-xs text-slate-500">Method: {record.check_in_method}</p>
                      </div>
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GPSAttendancePage;
